/* eslint-disable react/no-array-index-key */
import { Grid } from '@mui/material';
import MUIModal from '@mui/material/Modal';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { IconDeleteMS } from '../../../../../../../constants/icons';
import { CitiesAccordion } from '../../CitiesAccordion';
import { CitiesAccordionData } from '../../CitiesAccordion/types';
import {
  AddNewPoleButton,
  CancelButton,
  CoverageBox,
  CoverageDeleteButton,
  SaveCoverageButton,
  ScrollableGrid,
  StyledbBoxContainer,
  Title,
} from './styles';
import { useCoverageOperations } from './useCoverageOperations';

interface ModalAddingCoverageProps {
  isModalOpen: boolean;
  fetchCoverage: () => Promise<void>;
  onCloseModal: () => void;
  title: string;
}

export function ModalAddingCoverage({
  isModalOpen,
  onCloseModal,
  title,
  fetchCoverage,
}: ModalAddingCoverageProps): JSX.Element {
  const [citiesAccordions, setCitiesAccordions] = useState<
    CitiesAccordionData[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState<boolean[]>([]);
  const { notice } = useParams();
  const noticeId = parseInt(notice as string, 10);

  useEffect(() => {
    if (isModalOpen) {
      setCitiesAccordions([
        { name: '', selectedCity: '', displacementValue: '', microregions: [] },
      ]);
      setExpandedAccordions([true]);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) {
      setCitiesAccordions([]);
      setExpandedAccordions([]);
    }
  }, [isModalOpen]);

  const { createCoverage } = useCoverageOperations({
    noticeId,
    onSuccess: () => {
      onCloseModal?.();
      fetchCoverage();
    },
  });

  const handleCitiesDataChange = useCallback(
    (index: number, data: CitiesAccordionData) => {
      setCitiesAccordions((prev) => {
        const updated = [...prev];
        updated[index] = data;
        return updated;
      });
    },
    []
  );

  const handleAddNewPole = useCallback(() => {
    setCitiesAccordions((prev) => [
      ...prev,
      { name: '', selectedCity: '', displacementValue: '', microregions: [] },
    ]);
    setExpandedAccordions((prev) => [...prev, false]);
  }, []);

  const isSaveDisabled = useMemo(() => {
    if (isSaving) return true;

    const hasValidPole = citiesAccordions.some(
      (accordion) => accordion.selectedCity && accordion.displacementValue
    );
    return !hasValidPole;
  }, [citiesAccordions, isSaving]);

  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      const transformedData: CitiesAccordionData[] = citiesAccordions.map(
        (region) => ({
          name: region.name,
          cityId: region.cityId,
          selectedCity: region.selectedCity,
          displacementValue: region.displacementValue,
          microregions: region.microregions.map((subregion) => ({
            id: subregion.id,
            value: subregion.value || '',
            city_id: subregion.id || null,
            city: subregion,
          })),
        })
      );
      const success = await createCoverage(transformedData);
      if (success) {
        onCloseModal?.();
      }
    } finally {
      setIsSaving(false);
    }
  }, [citiesAccordions, createCoverage, onCloseModal]);

  const handleAccordionChange = useCallback(
    (index: number, isExpanded: boolean) => {
      setExpandedAccordions((prev) => {
        const updated = [...prev];
        updated[index] = isExpanded;
        return updated;
      });
    },
    []
  );

  const handleRemovePole = useCallback((index: number) => {
    setCitiesAccordions((prev) => prev.filter((_, i) => i !== index));
    setExpandedAccordions((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return (
    <MUIModal open={isModalOpen} onClose={onCloseModal}>
      <StyledbBoxContainer>
        <Title>{title}</Title>
        <ScrollableGrid>
          {citiesAccordions.map((accordionData, index) => (
            <CoverageBox key={index}>
              <CitiesAccordion
                onDataChange={(data) => handleCitiesDataChange(index, data)}
                expanded={expandedAccordions[index]}
                onExpandedChange={(isExpanded: boolean) =>
                  handleAccordionChange(index, isExpanded)
                }
              />
              {index > 0 && (
                <CoverageDeleteButton onClick={() => handleRemovePole(index)}>
                  {IconDeleteMS}
                </CoverageDeleteButton>
              )}
            </CoverageBox>
          ))}
        </ScrollableGrid>
        <Grid item container justifyContent="end" mt={2} xs={11.5}>
          <AddNewPoleButton onClick={handleAddNewPole} disabled={isSaving}>
            Novo polo
          </AddNewPoleButton>
        </Grid>
        <Grid item container justifyContent="center" mt={2}>
          <Grid item mr={2}>
            <CancelButton onClick={onCloseModal} disabled={isSaving}>
              Cancelar
            </CancelButton>
          </Grid>
          <Grid item ml={2}>
            <SaveCoverageButton onClick={handleSave} disabled={isSaveDisabled}>
              {isSaving ? 'Salvando...' : 'Salvar'}
            </SaveCoverageButton>
          </Grid>
        </Grid>
      </StyledbBoxContainer>
    </MUIModal>
  );
}
